import React, { useRef, useState, useCallback } from "react";
import Webcam from "react-webcam";
import { Camera, X, RotateCw, Check } from "lucide-react";
import { useI18nStore } from "@/stores/i18nStore";
import { i18nToast } from "@/utils/i18nToast";
import { dataUrlToFile } from "@/utils/dataUrlToFile";

interface CameraCaptureModalProps {
  onCapture: (file: File) => void;
  onCancel: () => void;
}

export const CameraCaptureModal: React.FC<CameraCaptureModalProps> = ({
  onCapture,
  onCancel,
}) => {
  const { t } = useI18nStore();
  const webcamRef = useRef<Webcam>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<"user" | "environment">("user");
  const [error, setError] = useState<string | null>(null);

  const capture = useCallback(() => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (imageSrc) {
      setCapturedImage(imageSrc);
    } else {
      i18nToast.error(
        t.photoCaptureFailed || "Could not use the photo. Please try again.",
      );
    }
  }, [webcamRef, t]);

  const handleConfirm = () => {
    if (!capturedImage) return;

    // This used fetch(dataUrl), which the CSP blocks; the rejected promise
    // went unhandled and left "Use Photo" doing nothing at all.
    let file: File;
    try {
      file = dataUrlToFile(capturedImage, `avatar-${Date.now()}.jpg`);
    } catch (err) {
      console.error("Photo conversion failed:", err);
      i18nToast.error(
        t.photoCaptureFailed || "Could not use the photo. Please try again.",
      );
      return;
    }
    onCapture(file);
  };

  const handleRetake = () => {
    setCapturedImage(null);
  };

  const toggleCamera = () => {
    setFacingMode((prev) => (prev === "user" ? "environment" : "user"));
  };

  const handleUserMediaError = (error: string | DOMException) => {
    console.error("Camera error:", error);
    setError(t.cameraAccessDenied || "Camera access denied");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            {t.takePhoto || "Take Photo"}
          </h2>
          <button
            onClick={onCancel}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* Camera/Preview Area */}
        <div className="relative bg-black aspect-video flex items-center justify-center">
          {error ? (
            <div className="text-white text-center p-6">
              <Camera className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg">{error}</p>
              <p className="text-sm mt-2 opacity-75">
                {t.checkCameraPermissions || "Please check camera permissions"}
              </p>
            </div>
          ) : capturedImage ? (
            <img
              src={capturedImage}
              alt="Captured"
              className="max-w-full max-h-full object-contain"
            />
          ) : (
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              videoConstraints={{
                facingMode,
                width: 1280,
                height: 720,
              }}
              onUserMediaError={handleUserMediaError}
              className="w-full h-full object-cover"
            />
          )}
        </div>

        {/* Actions */}
        <div className="p-6 bg-gray-50">
          {capturedImage ? (
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={handleRetake}
                className="px-6 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {t.retake || "Retake"}
              </button>
              <button
                onClick={handleConfirm}
                className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Check className="w-5 h-5" />
                {t.usePhoto || "Use Photo"}
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={toggleCamera}
                className="p-3 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                title={t.switchCamera || "Switch camera"}
              >
                <RotateCw className="w-5 h-5" />
              </button>
              <button
                onClick={capture}
                disabled={!!error}
                className="flex items-center gap-2 px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                <Camera className="w-5 h-5" />
                {t.capture || "Capture"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
